// services/interviewAuthService.js

class InterviewAuthService {
  constructor() {
    this.token = null;
    this.refreshToken = null;
    this.decodedToken = null;
  }

  // Store tokens after successful OTP verification
  setTokens(token, refreshToken) {
    this.token = token;
    this.refreshToken = refreshToken;

    // Decode the JWT token to extract interview details
    this.decodedToken = this.decodeJWT(token);

    // Store in sessionStorage for persistence
    sessionStorage.setItem("interviewAuthToken", token);
    sessionStorage.setItem("interviewRefreshToken", refreshToken);
  }

  // Get current access token
  getToken() {
    if (!this.token) {
      // Try to get from sessionStorage if not in memory
      this.token = sessionStorage.getItem("interviewAuthToken");
      //"eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VyX2lkIjoiNTM5MzdiODAtMmE0OC00YzQ3LWJmZjMtYzM1YTllZmZjYzk5IiwiY2FuZGlkYXRlX25hbWUiOiJVdGthcnNoIFNoZWtoYXIiLCJlbWFpbCI6InRlc3RhbnRwaHlAZ21haWwuY29tIiwiam9iX2lkIjpbImMxMmQ4MWFmLTg4YTAtNDE3ZC05ZTAxLTI1N2RiMTlmZmE2NyIsIjBhMjA5MWEwLTIwZGMtNGFjMy05Y2ZjLTcyMzE2N2M1ZWM3YyJdLCJpbnRlcnZpZXdfaWQiOlsiMGRmMjE4MzEtMjcxNy00MmE3LTg1YTgtOGFmNWNkNThiYjU2IiwiMGRmMjE4MzEtMjcxNy00MmE3LTg1YTgtOGFmNWNkNThiYjU1Il0sInBob25lX251bWJlciI6IjkxODQ5NTk0ODIxNCIsInVzZXJfcm9sZSI6ImNhbmRpZGF0ZSIsImxvZ2luX3R5cGUiOiJpbnRlcnZpZXciLCJleHAiOjE3NTU1ODg0NzAuNjY2NzczLCJ0eXBlIjoiYWNjZXNzIn0.7M3_o-ZHtYsCFoWRV70TykH_-Dn7Fu2CYAroDEtIwTQ";
    }
    return this.token;
  }

  // Get refresh token
  getRefreshToken() {
    if (!this.refreshToken) {
      // Try to get from sessionStorage if not in memory
      this.refreshToken = sessionStorage.getItem("interviewRefreshToken");
    }
    return this.refreshToken;
  }

  // Decode JWT token manually (without external libraries)
  decodeJWT(token) {
    try {
      const parts = token.split(".");

      if (parts.length !== 3) {
        throw new Error("Invalid JWT token format");
      }

      const payload = parts[1];
      const paddedPayload =
        payload + "=".repeat((4 - (payload.length % 4)) % 4);
      const base64 = paddedPayload.replace(/-/g, "+").replace(/_/g, "/");

      const jsonPayload =
        typeof atob === "function"
          ? atob(base64)
          : Buffer.from(base64, "base64").toString("utf-8");

      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error("Error decoding JWT token:", error);
      return null;
    }
  }

  // Get userId from decoded token
  getUserId() {
    if (!this.decodedToken && this.token) {
      this.decodedToken = this.decodeJWT(this.token);
    }
    return this.decodedToken?.user_id || null;
  }

  // Get jobId from decoded token
  getJobId() {
    if (!this.decodedToken && this.token) {
      this.decodedToken = this.decodeJWT(this.token);
    }
    return this.decodedToken?.job_id || null;
  }

  // Get interviewId from decoded token
  getInterviewId() {
    if (!this.decodedToken && this.token) {
      this.decodedToken = this.decodeJWT(this.token);
    }
    return this.decodedToken?.interview_id || null;
  }

  // Get email from decoded token
  getEmail() {
    if (!this.decodedToken && this.token) {
      this.decodedToken = this.decodeJWT(this.token);
    }
    return this.decodedToken?.email || null;
  }
  getCandidateName() {
    if (!this.decodedToken && this.token) {
      this.decodedToken = this.decodeJWT(this.token);
    }
    return this.decodedToken?.candidate_name || null;
  }
  // Get phone number from decoded token
  getPhoneNumber() {
    if (!this.decodedToken && this.token) {
      this.decodedToken = this.decodeJWT(this.token);
    }
    return this.decodedToken?.phone_number || null;
  }

  // Get user role from decoded token
  getUserRole() {
    if (!this.decodedToken && this.token) {
      this.decodedToken = this.decodeJWT(this.token);
    }
    return this.decodedToken?.user_role || null;
  }

  // Get login type from decoded token
  getLoginType() {
    if (!this.decodedToken && this.token) {
      this.decodedToken = this.decodeJWT(this.token);
    }
    return this.decodedToken?.login_type || null;
  }

  // Get all user info from decoded token
  getUserInfo() {
    if (!this.decodedToken && this.token) {
      this.decodedToken = this.decodeJWT(this.token);
    }
    return this.decodedToken;
  }

  // Check if token is expired
  isTokenExpired() {
    const token = this.getToken();

    if (!token) {
      return true;
    }

    if (!this.decodedToken) {
      this.decodedToken = this.decodeJWT(token);
    }

    if (!this.decodedToken || !this.decodedToken.exp) {
      console.warn("Token decoded but no expiration found");
      return true;
    }

    const currentTime = Math.floor(Date.now() / 1000);
    return this.decodedToken.exp < currentTime;
  }

  // Refresh the access token using refresh token
  async refreshAccessToken() {
    const refreshToken = this.getRefreshToken();

    if (!refreshToken) {
      throw new Error("No refresh token available");
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_ROOT_URL}/auth/api/v1/Authentication/interview/refresh`,
        {
          method: "POST",
          headers: {
            accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            refresh_token: refreshToken,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        this.setTokens(
          data.access_token || data.token,
          data.refresh_token || refreshToken
        );
        return data.access_token || data.token;
      } else {
        throw new Error("Failed to refresh token");
      }
    } catch (error) {
      console.error("Error refreshing token:", error);
      this.clearTokens();
      throw error;
    }
  }

  // Make authenticated API request with automatic token refresh
  // Make authenticated API request with automatic token refresh
  async makeAuthenticatedRequest(url, options = {}) {
    let token = this.getToken();

    // Check if token is expired and refresh if needed
    if (this.isTokenExpired()) {
      try {
        token = await this.refreshAccessToken();
      } catch (error) {
        throw new Error("Authentication failed. Please login again.");
      }
    }

    // Determine if we're dealing with FormData
    const isFormData = options.body instanceof FormData;

    // Build headers - don't set Content-Type for FormData
    const headers = {
      ...options.headers,
      Authorization: `Bearer ${token}`,
      accept: "application/json",
    };

    // Only set Content-Type for non-FormData requests
    if (!isFormData && !headers["Content-Type"]) {
      headers["Content-Type"] = "application/json";
    }

    // If Content-Type is explicitly set to undefined, remove it
    if (headers["Content-Type"] === undefined) {
      delete headers["Content-Type"];
    }

    const requestOptions = {
      ...options,
      headers,
    };

    try {
      const response = await fetch(url, requestOptions);

      // If we get 401, try to refresh token once
      if (response.status === 401) {
        try {
          token = await this.refreshAccessToken();
          requestOptions.headers["Authorization"] = `Bearer ${token}`;
          return await fetch(url, requestOptions);
        } catch (refreshError) {
          this.clearTokens();
          throw new Error("Authentication failed. Please login again.");
        }
      }

      return response;
    } catch (error) {
      console.error("API request failed:", error);
      throw error;
    }
  }

  // Clear all tokens (logout)
  clearTokens() {
    this.token = null;
    this.refreshToken = null;
    this.decodedToken = null;

    sessionStorage.removeItem("interviewAuthToken");
    sessionStorage.removeItem("interviewRefreshToken");
  }

  // Check if user is authenticated
  isAuthenticated() {
    const token = this.getToken();

    if (!token) {
      console.warn("No interview token found");
      return false;
    }

    const isExpired = this.isTokenExpired();

    if (isExpired) {
      console.warn("Interview token is expired");
      return false;
    }

    return true;
  }

  // Initialize service (call this on app startup)
  initialize() {
    try {
      // Try to load tokens from sessionStorage
      const token = sessionStorage.getItem("interviewAuthToken");
      const refreshToken = sessionStorage.getItem("interviewRefreshToken");

      if (token && refreshToken) {
        this.token = token;
        this.refreshToken = refreshToken;
        this.decodedToken = this.decodeJWT(token);

        if (this.decodedToken) {
        } else {
          console.warn("Failed to decode token, clearing tokens");
          this.clearTokens();
        }
      } else {
      }
    } catch (error) {
      console.error("Error initializing InterviewAuthService:", error);
      this.clearTokens();
    }
  }

  // Debug method to inspect token
  debugToken() {
    const token = this.getToken();

    if (token) {
      const parts = token.split(".");

      if (parts.length === 3) {
        try {
          const header = JSON.parse(atob(parts[0]));
        } catch (e) {
          console.log("Could not decode header:", e.message);
        }
      }
    }
  }
}

// Create singleton instance
const interviewAuthService = new InterviewAuthService();

export default interviewAuthService;
